import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import type {
  CartProductItem,
  ProductSelection,
  SubItem,
} from "@/features/cart/types";
import type { ConfigurationItemAddon } from "../types/configuration-item-addon.type";
import type {
  SubItemPricingItem,
  SubItemsPricing,
} from "../types/sub-Items-pricing.type";
import { addonsToSubItems } from "./sub-items";

// Bridge between the view-local addon selection and the two collections the
// cart keeps it in (MPL-3829). The addon catalog of a diet lists both the
// catering's own addons and Foodango products of other merchants; the view
// treats them alike, the cart does not: the catering's addons are `subItems`
// of the diet position, products of another merchant are `productItems`.

// A catalog addon owned by a merchant other than the diet's catering is a
// product. Owner-less rows predate the merchant sync and stay addons.
export const isProductOfOtherMerchant = (
  addon: Pick<AddonDietAddonsRead, "merchantId">,
  dietMerchantId: string,
): boolean => !!addon.merchantId && addon.merchantId !== dietMerchantId;

// A failed catalog call can resolve with an error body instead of the list (the
// fetch layer does not throw on 5xx); without a catalog nothing can be told
// apart, so treat it as empty rather than crash the configuration view.
const indexAddons = (categories: DietAddonGroupDietAddonsRead[]) => {
  const byId = new Map<string, AddonDietAddonsRead>();
  for (const category of Array.isArray(categories) ? categories : []) {
    for (const addon of category.addons) {
      byId.set(addon.id, addon);
    }
  }
  return byId;
};

// Outgoing: route every selected entry to the collection the cart expects.
// An entry whose addon is not in the catalog cannot be told apart, so it is
// sent as a sub-item — the cart rejects it either way, with the reason.
export const splitAddonEntries = ({
  entries,
  categories,
  dietMerchantId,
}: {
  entries: ConfigurationItemAddon[];
  categories: DietAddonGroupDietAddonsRead[];
  dietMerchantId: string;
}): { subItems: SubItem[]; productItems: ProductSelection[] } => {
  const addonsById = indexAddons(categories);
  const addonEntries: ConfigurationItemAddon[] = [];
  const productItems: ProductSelection[] = [];

  for (const entry of entries) {
    const addon = addonsById.get(entry.addonId);
    if (addon?.merchantId && isProductOfOtherMerchant(addon, dietMerchantId)) {
      productItems.push({
        productId: entry.addonId,
        merchantId: addon.merchantId,
        date: entry.date,
        quantity: entry.quantity,
      });
      continue;
    }
    addonEntries.push(entry);
  }

  return { subItems: addonsToSubItems(addonEntries), productItems };
};

// Incoming: the position's products as store entries, next to the ones from its
// sub-items. A product is an addon of the same catalog, so the entry is keyed
// by the product id like any other.
export const productItemsToAddons = (
  productItems: CartProductItem[],
): ConfigurationItemAddon[] =>
  productItems.map(({ productItem }) => ({
    addonId: productItem.productId,
    date: productItem.date,
    quantity: productItem.quantity,
  }));

const zero = { gross: 0, net: 0, tax: 0 };

const add = (
  left: { gross: number; net: number; tax: number },
  right: { gross: number; net: number; tax: number },
) => ({
  gross: left.gross + right.gross,
  net: left.net + right.net,
  tax: left.tax + right.tax,
});

const toPricing = (items: SubItemPricingItem[]): SubItemsPricing | null =>
  items.length === 0
    ? null
    : {
        items,
        before: items.reduce((sum, item) => add(sum, item.before), zero),
        final: items.reduce((sum, item) => add(sum, item.final), zero),
        deposit: items.reduce((sum, item) => add(sum, item.deposit), zero),
      };

// The products' quote in the shape of `subItemsPricing`, so the addon summary
// prices both collections through one path. Section semantics match: `final`
// carries the deposit, `before` does not.
export const productItemsToPricing = (
  productItems: CartProductItem[],
): SubItemsPricing | null =>
  toPricing(
    productItems.map(({ productItem, pricing }) => ({
      type: "addon",
      id: productItem.productId,
      date: productItem.date,
      quantity: productItem.quantity,
      before: pricing.before,
      final: pricing.final,
      deposit: pricing.deposit,
    })),
  );

// One quote for the whole selection of a position: the addons' `subItemsPricing`
// and the products' pricing side by side. Null when neither has anything.
export const mergeAddonsPricing = (
  ...pricings: (SubItemsPricing | null | undefined)[]
): SubItemsPricing | null =>
  toPricing(pricings.flatMap((pricing) => pricing?.items ?? []));

// Quote of the products picked on the configuration view. The price endpoint
// knows nothing about products, so entries the cart has not quoted yet are
// estimated from the catalog the way the backend prices them: list price plus
// the deposits, per unit (products carry no promotions yet, MPL-3914). An
// entry the cart already quoted at the same quantity keeps the cart's amounts.
export const estimateProductsPricing = ({
  entries,
  categories,
  quoted = [],
}: {
  entries: ConfigurationItemAddon[];
  categories: DietAddonGroupDietAddonsRead[];
  quoted?: CartProductItem[];
}): SubItemsPricing | null => {
  const addonsById = indexAddons(categories);
  const quotedByKey = new Map(
    quoted.map((item) => [
      `${item.productItem.productId}|${item.productItem.date}`,
      item,
    ]),
  );

  const items: SubItemPricingItem[] = [];
  for (const entry of entries) {
    const addon = addonsById.get(entry.addonId);
    if (!addon) {
      continue;
    }

    const quote = quotedByKey.get(`${entry.addonId}|${entry.date}`);
    if (quote && quote.productItem.quantity === entry.quantity) {
      items.push({
        type: "addon",
        id: entry.addonId,
        date: entry.date,
        quantity: entry.quantity,
        before: quote.pricing.before,
        final: quote.pricing.final,
        deposit: quote.pricing.deposit,
      });
      continue;
    }

    const unitDeposit = addon.deposits.reduce(
      (sum, current) => sum + current.cost,
      0,
    );
    const before = addon.price * entry.quantity;
    const deposit = unitDeposit * entry.quantity;
    items.push({
      type: "addon",
      id: entry.addonId,
      date: entry.date,
      quantity: entry.quantity,
      before: { gross: before, net: 0, tax: 0 },
      final: { gross: before + deposit, net: 0, tax: 0 },
      deposit: { gross: deposit, net: 0, tax: 0 },
    });
  }

  return toPricing(items);
};

// Only the entries that the split would send as products.
export const productEntries = ({
  entries,
  categories,
  dietMerchantId,
}: {
  entries: ConfigurationItemAddon[];
  categories: DietAddonGroupDietAddonsRead[];
  dietMerchantId: string;
}): ConfigurationItemAddon[] => {
  const addonsById = indexAddons(categories);
  return entries.filter((entry) => {
    const addon = addonsById.get(entry.addonId);
    return !!addon && isProductOfOtherMerchant(addon, dietMerchantId);
  });
};
